import { Building2, CreditCard, Heart } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CARD_TYPE } from '../../admin/modules/GestionCartesAccesEtBienEtre/shared/constants'

export const AMICALE_VALIDATION_TAB = {
  HOTEL: 'hotel',
  CARTE_ACCES: CARD_TYPE.ACCES,
  BIEN_ETRE: CARD_TYPE.BIEN_ETRE,
}

const TAB_META = {
  [AMICALE_VALIDATION_TAB.HOTEL]: {
    labelKey: 'amicales.products.hotel',
    hintKey: 'amicales.products.hotelHint',
    Icon: Building2,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    activeRing: 'ring-sky-200',
    activeBar: 'bg-sky-500',
    activeBg: 'bg-sky-50/80',
  },
  [AMICALE_VALIDATION_TAB.CARTE_ACCES]: {
    labelKey: 'amicales.products.accessCard',
    hintKey: 'amicales.products.accessHint',
    Icon: CreditCard,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    activeRing: 'ring-violet-200',
    activeBar: 'bg-violet-500',
    activeBg: 'bg-violet-50/80',
  },
  [AMICALE_VALIDATION_TAB.BIEN_ETRE]: {
    labelKey: 'amicales.products.wellness',
    hintKey: 'amicales.products.wellnessHint',
    Icon: Heart,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    activeRing: 'ring-emerald-200',
    activeBar: 'bg-emerald-500',
    activeBg: 'bg-emerald-50/80',
  },
}

const TAB_ORDER = [
  AMICALE_VALIDATION_TAB.HOTEL,
  AMICALE_VALIDATION_TAB.CARTE_ACCES,
  AMICALE_VALIDATION_TAB.BIEN_ETRE,
]

const AmicaleValidationProductTab = ({ tabId, active, onSelect }) => {
  const { t } = useTranslation()
  const meta = TAB_META[tabId]
  if (!meta) return null

  const { labelKey, hintKey, Icon, iconBg, iconColor, activeRing, activeBar, activeBg } = meta
  const label = t(labelKey)
  const hint = t(hintKey)

  return (
    <button
      type='button'
      role='tab'
      aria-selected={active}
      aria-label={label}
      onClick={() => onSelect(tabId)}
      className={[
        'group relative flex min-h-[72px] flex-1 items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all duration-200 sm:px-4',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
        active
          ? `border-transparent shadow-md ring-1 ${activeRing} ${activeBg}`
          : 'border-transparent bg-white/60 hover:border-slate-200 hover:bg-white hover:shadow-sm',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200',
          iconBg,
          active ? 'scale-105 shadow-sm' : 'scale-100 group-hover:scale-105',
        ].join(' ')}
        aria-hidden
      >
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={2.25} />
      </span>
      <span className='min-w-0 flex-1'>
        <span
          className={[
            'block truncate text-sm font-semibold leading-tight sm:text-[0.9375rem]',
            active ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-800',
          ].join(' ')}
        >
          {label}
        </span>
        <span
          className={[
            'mt-0.5 block truncate text-[11px] leading-tight sm:text-xs',
            active ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-500',
          ].join(' ')}
        >
          {hint}
        </span>
      </span>
      <span
        className={[
          'absolute bottom-0 left-1/2 h-1 -translate-x-1/2 rounded-full transition-all duration-200',
          active ? `w-12 ${activeBar}` : 'w-0 bg-transparent',
        ].join(' ')}
        aria-hidden
      />
    </button>
  )
}

const AmicaleValidationProductTabs = ({ activeTab, onChange }) => {
  const { t } = useTranslation()
  return (
  <header className='sticky top-[60px] z-40 w-full border-b border-slate-200/90 bg-gradient-to-b from-white via-white to-slate-50/90 shadow-sm backdrop-blur-md'>
    <div className='mx-auto w-full max-w-[1400px] px-3 py-4 md:px-6'>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <div>
          <p className='text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400'>
            {t('amicales.products.validationEyebrow')}
          </p>
          <h2 className='text-base font-bold text-slate-900 md:text-lg'>
            {t('amicales.products.validationTitle')}
          </h2>
        </div>
        <span className='hidden rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 sm:inline-flex'>
          {t('amicales.products.productCount')}
        </span>
      </div>

      <div
        role='tablist'
        aria-label={t('amicales.products.validationTypeAria')}
        className='grid gap-2 rounded-2xl border border-slate-200/80 bg-slate-100/70 p-1.5 shadow-inner sm:grid-cols-3'
      >
        {TAB_ORDER.map((tabId) => (
          <AmicaleValidationProductTab
            key={tabId}
            tabId={tabId}
            active={activeTab === tabId}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  </header>
  )
}

export default AmicaleValidationProductTabs
